// Controles pequenos reutilizados nas telas de encaminhamento.

import type { ReactNode } from "react";
import { cn } from "@/lib/utils";

interface ToggleProps {
  readonly id: string;
  readonly checked: boolean;
  readonly onChange: (checked: boolean) => void;
  readonly label: ReactNode;
  readonly hint?: ReactNode;
  readonly disabled?: boolean;
}

/** Interruptor acessivel (role=switch). */
export function Toggle({ id, checked, onChange, label, hint, disabled }: ToggleProps): JSX.Element {
  return (
    <div className="flex items-start justify-between gap-4">
      <div className="min-w-0">
        <label htmlFor={id} className="text-sm font-medium text-foreground">
          {label}
        </label>
        {hint && <p className="mt-0.5 text-xs text-muted-foreground">{hint}</p>}
      </div>
      <button
        id={id}
        type="button"
        role="switch"
        aria-checked={checked}
        disabled={disabled}
        onClick={() => onChange(!checked)}
        className={cn(
          "relative mt-0.5 inline-flex h-5 w-9 shrink-0 items-center rounded-full border transition-colors duration-[120ms]",
          "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:opacity-40",
          checked
            ? "border-primary/40 bg-primary"
            : "border-[hsl(var(--border-strong))] bg-[hsl(var(--surface-raised))]",
        )}
      >
        <span
          className={cn(
            "inline-block h-3.5 w-3.5 rounded-full bg-white shadow transition-transform duration-[120ms]",
            checked ? "translate-x-[18px]" : "translate-x-[3px]",
          )}
        />
      </button>
    </div>
  );
}

interface SegmentedProps<T extends string> {
  readonly label: string;
  readonly value: T;
  readonly options: ReadonlyArray<{ value: T; label: string }>;
  readonly onChange: (value: T) => void;
  readonly size?: "sm" | "md";
}

/** Grupo de botoes exclusivos (mesmo visual do seletor de periodo). */
export function Segmented<T extends string>({
  label,
  value,
  options,
  onChange,
  size = "md",
}: SegmentedProps<T>): JSX.Element {
  return (
    <div
      role="radiogroup"
      aria-label={label}
      className="inline-flex items-center gap-0.5 rounded-lg border border-[hsl(var(--border))] bg-[hsl(var(--surface))] p-0.5"
    >
      {options.map((option) => {
        const active = option.value === value;
        return (
          <button
            key={option.value}
            type="button"
            role="radio"
            aria-checked={active}
            onClick={() => onChange(option.value)}
            className={cn(
              "rounded-md font-medium transition-all duration-[120ms]",
              "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[hsl(var(--ring))]",
              size === "sm" ? "px-2 py-0.5 text-[11px]" : "px-2.5 py-1 text-xs",
              active
                ? "border border-[hsl(var(--border-strong))] bg-[hsl(var(--surface-raised))] text-foreground shadow-sm"
                : "text-muted-foreground hover:bg-[hsl(var(--surface-raised)/0.5)] hover:text-foreground",
            )}
          >
            {option.label}
          </button>
        );
      })}
    </div>
  );
}

export const selectClassName = cn(
  "h-9 w-full rounded-md border border-[hsl(var(--border-strong))]",
  "bg-[hsl(var(--surface-raised))] px-3 text-sm text-foreground",
  "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
  "disabled:cursor-not-allowed disabled:opacity-50",
);

export function InlineError({ message }: { readonly message: string | null }): JSX.Element | null {
  if (!message) return null;
  return (
    <p
      role="alert"
      className="rounded-md border border-destructive/30 bg-destructive/10 px-3 py-2 text-xs text-destructive"
    >
      {message}
    </p>
  );
}

export function PlatformBadge({
  platform,
}: {
  readonly platform: "telegram" | "whatsapp";
}): JSX.Element {
  return (
    <span
      className={cn(
        "inline-flex shrink-0 items-center rounded-md px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wider",
        platform === "telegram"
          ? "bg-sky-500/15 text-sky-300"
          : "bg-emerald-500/15 text-emerald-300",
      )}
    >
      {platform === "telegram" ? "TG" : "WA"}
    </span>
  );
}
